import React, { useState, useMemo, useEffect, useRef } from 'react';
import { db, auth } from '../config/firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import useAdminStore from '../store/adminStore';

const FileTextIcon = ({ size = 20, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
);

const XIcon = ({ size = 20, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);

const SaveIcon = ({ size = 20, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
);

type CustomerMode = 'existing' | 'manual';

interface CoachInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Bestehende Rechnung bearbeiten — sonst Neu-Anlage */
    editingInvoice?: any | null;
}

const CoachInvoiceModal: React.FC<CoachInvoiceModalProps> = ({ isOpen, onClose, editingInvoice = null }) => {
    const { crmClients, adminTeam } = useAdminStore();
    const skipEmailPrefillRef = useRef(false);
    
    const [customerMode, setCustomerMode] = useState<CustomerMode>('existing');
    const [clientId, setClientId] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [manualFirma, setManualFirma] = useState('');
    const [manualName, setManualName] = useState('');
    const [manualStrasse, setManualStrasse] = useState('');
    const [manualPlz, setManualPlz] = useState('');
    const [manualOrt, setManualOrt] = useState('');
    const [manualLand, setManualLand] = useState('DE');

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [serviceDate, setServiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [invoiceTitle, setInvoiceTitle] = useState('Individuelles Coaching');
    const [netAmount, setNetAmount] = useState<number | ''>('');
    /** Ausland: Reverse Charge / keine MwSt. ausweisen — nicht MwSt-Befreiung */
    const [reverseChargeAbroad, setReverseChargeAbroad] = useState(false);
    /** Nachbearbeitung / Korrektur einer bereits versendeten Rechnung */
    const [isCorrection, setIsCorrection] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isEditMode = !!(editingInvoice && editingInvoice.id);

    const selectableClients = useMemo(() => {
        return (crmClients || [])
            .filter(c => !c.isArchived && !c.isDeleted && c.isActive !== false)
            .map(c => {
                const firmenname = c.firmenname || c.clientData?.salonname || c.clientData?.firmenname || '';
                const vorname = c.vorname || c.clientData?.vorname || '';
                const nachname = c.nachname || c.clientData?.nachname || '';
                const fullName = `${vorname} ${nachname}`.trim() || 'Kein Name';
                const email = String(
                    c.email || c.clientData?.mailadresse || c.clientData?.email || ''
                ).trim();
                
                return {
                    id: c.id,
                    displayName: firmenname ? `${fullName} (${firmenname})` : fullName,
                    fullName,
                    firmenname,
                    email,
                    sortName: fullName.toLowerCase(),
                    rawClient: c
                };
            })
            .sort((a, b) => a.sortName.localeCompare(b.sortName));
    }, [crmClients]);

    // Öffnen: Prefill bei Edit, Reset bei Neu
    useEffect(() => {
        if (!isOpen) return;

        if (editingInvoice?.id) {
            skipEmailPrefillRef.current = true;
            const isExt = editingInvoice.isExternalCustomer === true || !editingInvoice.clientId;
            setCustomerMode(isExt ? 'manual' : 'existing');
            setClientId(editingInvoice.clientId || '');
            setRecipientEmail(
                String(editingInvoice.recipientEmail || editingInvoice.clientEmail || '').trim()
            );
            setManualFirma(String(editingInvoice.clientFirma || '').trim());
            setManualName(String(editingInvoice.clientName || '').trim());
            setManualStrasse(String(editingInvoice.clientStrasse || '').trim());
            setManualPlz(String(editingInvoice.clientPlz || '').trim());
            setManualOrt(String(editingInvoice.clientOrt || '').trim());
            setManualLand(String(editingInvoice.clientLand || 'DE').trim() || 'DE');
            setInvoiceNumber(String(editingInvoice.invoiceNumber || ''));
            setServiceDate(
                String(editingInvoice.serviceDate || new Date().toISOString().split('T')[0]).slice(0, 10)
            );
            setInvoiceTitle(String(editingInvoice.invoiceTitle || 'Individuelles Coaching'));
            setNetAmount(
                editingInvoice.netAmount !== undefined && editingInvoice.netAmount !== null
                    ? Number(editingInvoice.netAmount)
                    : ''
            );
            setReverseChargeAbroad(editingInvoice.reverseCharge === true);
            setIsCorrection(editingInvoice.isCorrection === true);
            return;
        }

        skipEmailPrefillRef.current = false;
        setCustomerMode('existing');
        setClientId('');
        setRecipientEmail('');
        setManualFirma('');
        setManualName('');
        setManualStrasse('');
        setManualPlz('');
        setManualOrt('');
        setManualLand('DE');
        setInvoiceNumber('');
        setServiceDate(new Date().toISOString().split('T')[0]);
        setInvoiceTitle('Individuelles Coaching');
        setNetAmount('');
        setReverseChargeAbroad(false);
        setIsCorrection(false);
    }, [isOpen, editingInvoice]);

    // Bestehender Mandant: E-Mail nur bei Mandanten-Wechsel vorfüllen — nie bei CRM-Reload überschreiben
    const lastPrefillClientIdRef = useRef<string>('');
    useEffect(() => {
        if (customerMode !== 'existing' || !clientId) return;
        if (skipEmailPrefillRef.current) {
            skipEmailPrefillRef.current = false;
            lastPrefillClientIdRef.current = clientId;
            return;
        }
        if (lastPrefillClientIdRef.current === clientId) return;
        lastPrefillClientIdRef.current = clientId;
        const selected = selectableClients.find(c => c.id === clientId);
        if (selected) setRecipientEmail(selected.email || '');
    }, [customerMode, clientId, selectableClients]);

    const canSave =
        !!invoiceNumber &&
        !!serviceDate &&
        !!invoiceTitle &&
        netAmount !== '' &&
        !!recipientEmail.trim() &&
        (customerMode === 'existing' ? !!clientId : !!manualName.trim());

    const resetAndClose = () => {
        onClose();
    };

    const handleSave = async () => {
        if (!canSave) {
            alert("Bitte fülle alle Pflichtfelder aus.");
            return;
        }

        const emailTrim = recipientEmail.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailTrim || !emailRegex.test(emailTrim)) {
            alert('Bitte eine gültige Empfänger-E-Mail eingeben (manuelle Adresse hat Vorrang).');
            return;
        }

        setIsSaving(true);
        try {
            const user = auth.currentUser;
            const currentProfile = adminTeam.find(m => m.id === user?.uid || m.email === user?.email);
            const selectedClientObj = selectableClients.find(c => c.id === clientId);
            const raw = selectedClientObj?.rawClient;

            const net = Number(netAmount);
            const reverseCharge = reverseChargeAbroad === true;
            const vatRate = reverseCharge ? 0 : 19;
            const grossAmount = reverseCharge ? net : Math.round(net * 1.19 * 100) / 100;

            const baseCoach = {
                // coachId = Auth-UID (Rules); bei Edit bestehende Zuordnung behalten
                coachId: isEditMode
                    ? (editingInvoice?.coachId || user?.uid || 'unknown_coach')
                    : (user?.uid || 'unknown_coach'),
                coachName: currentProfile?.name || editingInvoice?.coachName || 'Unbekannter Coach',
                coachEmail: currentProfile?.email || user?.email || editingInvoice?.coachEmail || '',
                invoiceNumber: invoiceNumber,
                serviceDate: serviceDate,
                invoiceTitle: invoiceTitle,
                netAmount: net,
                grossAmount,
                reverseCharge,
                vatRate,
                isCorrection: isCorrection === true,
            };

            const payload =
                customerMode === 'manual'
                    ? {
                          ...baseCoach,
                          isExternalCustomer: true,
                          clientId: null as string | null,
                          clientFirma: manualFirma.trim(),
                          clientName: manualName.trim(),
                          clientStrasse: manualStrasse.trim(),
                          clientPlz: manualPlz.trim(),
                          clientOrt: manualOrt.trim(),
                          clientLand: (manualLand || 'DE').trim() || 'DE',
                          clientEmail: emailTrim,
                          recipientEmail: emailTrim,
                      }
                    : {
                          ...baseCoach,
                          isExternalCustomer: false,
                          clientId: clientId,
                          clientName: selectedClientObj?.fullName || selectedClientObj?.displayName || editingInvoice?.clientName || 'Unbekannter Mandant',
                          clientFirma:
                              selectedClientObj?.firmenname
                              || raw?.firmenname
                              || raw?.clientData?.salonname
                              || raw?.clientData?.firmenname
                              || editingInvoice?.clientFirma
                              || '',
                          clientStrasse: String(raw?.clientData?.strasse || raw?.strasse || editingInvoice?.clientStrasse || '').trim(),
                          clientPlz: String(raw?.clientData?.plz || raw?.plz || editingInvoice?.clientPlz || '').trim(),
                          clientOrt: String(raw?.clientData?.ort || raw?.ort || editingInvoice?.clientOrt || '').trim(),
                          clientLand: String(raw?.clientData?.land || raw?.land || editingInvoice?.clientLand || 'DE').trim() || 'DE',
                          clientEmail: emailTrim,
                          recipientEmail: emailTrim,
                      };

            if (isEditMode) {
                await updateDoc(doc(db, 'coach_manual_invoices', editingInvoice.id), {
                    ...payload,
                    updatedAt: serverTimestamp(),
                });
            } else {
                await addDoc(collection(db, 'coach_manual_invoices'), {
                    ...payload,
                    createdAt: serverTimestamp(),
                });
            }

            resetAndClose();

        } catch (error) {
            console.error("Fehler beim Speichern der Rechnung:", error);
            alert("Fehler beim Speichern der Rechnung.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const inputClass =
        'w-full bg-[#15171e] border border-slate-700 text-white rounded-xl p-4 outline-none focus:border-[#E6C299] transition-colors';
    const labelClass = 'text-[10px] uppercase tracking-widest text-slate-400 font-bold ml-1';

    return (
        <div className="fixed inset-0 z-[100000] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8 animate-fade-in">
            <div className="bg-[#08090b] border border-slate-800 rounded-3xl w-full max-w-2xl flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.8)] relative overflow-hidden max-h-[95vh]">
                
                <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-[#0f1015] shrink-0">
                    <h2 className="text-[#E6C299] font-black text-xl uppercase tracking-widest flex items-center gap-3">
                        <FileTextIcon size={24} /> {isEditMode ? 'Rechnung bearbeiten' : 'Neue Rechnung Erstellen'}
                    </h2>
                    <button onClick={resetAndClose} className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 p-2 rounded-xl transition-colors border border-slate-700">
                        <XIcon size={20} />
                    </button>
                </div>
                
                <div className="p-6 md:p-8 space-y-6 overflow-y-auto">

                    {/* Toggle: Bestehender Mandant | Fremdkunde */}
                    <div className="space-y-2">
                        <label className={labelClass}>Empfänger</label>
                        <div className="flex rounded-xl border border-slate-700 overflow-hidden bg-[#15171e]">
                            <button
                                type="button"
                                onClick={() => setCustomerMode('existing')}
                                className={`flex-1 py-3 px-3 text-[10px] font-black uppercase tracking-widest transition-colors ${
                                    customerMode === 'existing'
                                        ? 'bg-[#E6C299] text-black'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                                }`}
                            >
                                Bestehender Mandant
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setCustomerMode('manual');
                                    setClientId('');
                                }}
                                className={`flex-1 py-3 px-3 text-[10px] font-black uppercase tracking-widest transition-colors border-l border-slate-700 ${
                                    customerMode === 'manual'
                                        ? 'bg-[#E6C299] text-black'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                                }`}
                            >
                                Fremdkunde / Manuell
                            </button>
                        </div>
                    </div>

                    {customerMode === 'existing' ? (
                        <>
                            <div className="space-y-2">
                                <label className={labelClass}>Mandant / Salon</label>
                                <select 
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    className={`${inputClass} appearance-none cursor-pointer font-bold`}
                                >
                                    <option value="" disabled>-- Bitte Mandant auswählen --</option>
                                    {selectableClients.map(c => (
                                        <option key={c.id} value={c.id}>{c.displayName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Empfänger-E-Mail (Override möglich)</label>
                                <input
                                    type="email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    placeholder="wird aus Mandantendaten geladen"
                                    className={inputClass}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <label className={labelClass}>Firma / Salonname</label>
                                <input
                                    type="text"
                                    value={manualFirma}
                                    onChange={(e) => setManualFirma(e.target.value)}
                                    placeholder="Salon / Firma"
                                    className={inputClass}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Name / Ansprechpartner *</label>
                                <input
                                    type="text"
                                    value={manualName}
                                    onChange={(e) => setManualName(e.target.value)}
                                    placeholder="Vor- und Nachname"
                                    className={inputClass}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={labelClass}>Straße & Hausnummer (optional)</label>
                                <input
                                    type="text"
                                    value={manualStrasse}
                                    onChange={(e) => setManualStrasse(e.target.value)}
                                    placeholder="falls bekannt — sonst leer lassen"
                                    className={inputClass}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className={labelClass}>PLZ (optional)</label>
                                    <input
                                        type="text"
                                        value={manualPlz}
                                        onChange={(e) => setManualPlz(e.target.value)}
                                        placeholder="—"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <label className={labelClass}>Ort (optional)</label>
                                    <input
                                        type="text"
                                        value={manualOrt}
                                        onChange={(e) => setManualOrt(e.target.value)}
                                        placeholder="—"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className={labelClass}>Land (optional)</label>
                                    <input
                                        type="text"
                                        value={manualLand}
                                        onChange={(e) => setManualLand(e.target.value)}
                                        placeholder="DE"
                                        className={inputClass}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className={labelClass}>E-Mail-Adresse *</label>
                                    <input
                                        type="email"
                                        value={recipientEmail}
                                        onChange={(e) => setRecipientEmail(e.target.value)}
                                        placeholder="kunde@beispiel.de"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-500 ml-1">
                                Für No-Shows / Meetings reichen Name, Salon und E-Mail — Anschrift darf fehlen.
                            </p>
                        </>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className={labelClass}>Rechnungs-Nr.</label>
                            <input 
                                type="text"
                                placeholder="z.B. RE-2026-001"
                                value={invoiceNumber}
                                onChange={(e) => setInvoiceNumber(e.target.value)}
                                className={`${inputClass} font-mono`}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={labelClass}>Leistungsdatum</label>
                            <input 
                                type="date"
                                value={serviceDate}
                                onChange={(e) => setServiceDate(e.target.value)}
                                className={`${inputClass} font-mono`}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className={labelClass}>Leistungsbeschreibung</label>
                        <input 
                            type="text"
                            placeholder="z.B. Individuelles Coaching Q3"
                            value={invoiceTitle}
                            onChange={(e) => setInvoiceTitle(e.target.value)}
                            className={inputClass}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest text-[#E6C299] font-bold ml-1">Betrag (Netto in Euro)</label>
                        <div className="relative">
                            <input 
                                type="number"
                                placeholder="0.00"
                                value={netAmount}
                                onChange={(e) => setNetAmount(e.target.value ? Number(e.target.value) : '')}
                                className="w-full bg-slate-900 border border-[#E6C299]/50 text-[#E6C299] text-xl font-black rounded-xl p-4 pl-12 outline-none focus:border-[#E6C299] transition-colors font-mono"
                            />
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#E6C299] font-black text-xl">€</span>
                        </div>
                        <p className="text-slate-500 text-[10px] ml-1 mt-1">
                            {reverseChargeAbroad
                                ? 'Ausland: keine deutsche MwSt. ausgewiesen (Betrag in Euro).'
                                : 'Standard DE: 19 % MwSt. wird auf der Rechnung ausgewiesen.'}
                        </p>
                    </div>

                    <label className="flex items-start gap-3 rounded-xl border border-slate-700 bg-[#15171e] p-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={reverseChargeAbroad}
                            onChange={(e) => setReverseChargeAbroad(e.target.checked)}
                            className="mt-1 rounded border-slate-600"
                        />
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-slate-200">
                                Ausland / Reverse Charge (§ 13b) — ohne MwSt.-Ausweis
                            </span>
                            <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">
                                Nur für Rechnungen an Empfänger im Ausland. Wir sind nicht MwSt.-befreit —
                                hier wird lediglich keine deutsche Mehrwertsteuer ausgewiesen (Steuerschuld
                                beim Leistungsempfänger). Inland: Haken weglassen → 19 % MwSt.
                            </span>
                        </span>
                    </label>

                    <label className="flex items-start gap-3 rounded-xl border border-amber-700/40 bg-amber-950/20 p-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isCorrection}
                            onChange={(e) => setIsCorrection(e.target.checked)}
                            className="mt-1 rounded border-slate-600"
                        />
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-amber-200">
                                Nachbearbeitung / Korrektur
                            </span>
                            <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">
                                Für bereits versendete Rechnungen: Kennzeichnung als Korrektur
                                (gleiche Rechnungsnummer bleibt). Danach erneut per Mail versenden.
                            </span>
                        </span>
                    </label>

                </div>

                <div className="p-6 border-t border-slate-800 bg-[#0f1015] flex justify-end gap-4 shrink-0">
                    <button 
                        onClick={resetAndClose}
                        className="px-6 py-3 rounded-xl text-slate-300 text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-colors"
                    >
                        Abbrechen
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={isSaving || !canSave}
                        className="flex items-center gap-2 px-8 py-3 bg-[#E6C299] hover:bg-white text-black rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(230,194,153,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Speichere...' : <><SaveIcon size={16} /> {isEditMode ? 'Änderungen speichern' : 'Rechnung Speichern'}</>}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CoachInvoiceModal;
